import React, { useEffect, useMemo, useState } from 'react';

import { getAddressInfo } from 'bitcoin-address-validation';
import { ArrowRightLeft, Circle, CircleDot } from 'lucide-react';

import { Button } from '@/components/shadcn/ui/button';
import { supportedBtcAddressTypes } from '@/hooks/useValidateAddress';
import { NETWORK, useBtcWallet } from '@/networks/btc-like/btc';
import { NetworkNet } from '@/networks/btc-like/common';
import { cn, truncateMidAddressInputValue } from '@/utils';

const HeaderWalletDrawerBtcWalletDetailsContent: React.FC = () => {
  const { availableAddresses, switchNetwork, address, setAddress, getNetwork } = useBtcWallet();

  const [btcNetwork, setBtcNetwork] = useState<Awaited<ReturnType<NonNullable<typeof getNetwork>>>>();

  const shouldSwitchTo = useMemo(() => {
    if (!btcNetwork) return;

    if (NETWORK.Network === NetworkNet.Mainnet && btcNetwork !== 'livenet') {
      return { network: 'livenet', text: 'Mainnet' };
    }

    if (NETWORK.Network === NetworkNet.Testnet && btcNetwork !== 'testnet') {
      return { network: 'testnet', text: 'Testnet' };
    }
  }, [btcNetwork]);

  const switchNetworkRequest = async () => {
    if (!shouldSwitchTo || !switchNetwork) {
      return;
    }

    try {
      const networkToSwitch = shouldSwitchTo.network as Awaited<ReturnType<NonNullable<typeof getNetwork>>>;

      await switchNetwork(networkToSwitch);
      setBtcNetwork(networkToSwitch);
    } catch {
      // mute
    }
  };

  useEffect(() => {
    if (getNetwork) {
      getNetwork()
        .then(setBtcNetwork)
        .catch(() => null);
    }
  }, [getNetwork]);

  return (
    <>
      {availableAddresses.map((account) => {
        const addressInfo = getAddressInfo(account);

        const isAddressTypeSupported = supportedBtcAddressTypes.includes(addressInfo.type);

        const isAccountSelected = account === address;

        return (
          <Button
            asChild
            key={'header-wallet-drawer-btc-wallet-details-'.concat(account)}
            variant="secondary"
            className="text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation();
              if (isAddressTypeSupported) setAddress(account);
            }}
          >
            <div
              className={cn(
                'font-roboto flex w-full cursor-pointer items-center justify-start gap-2 rounded-xl py-6 text-base',
                { 'text-primary': isAccountSelected }
              )}
            >
              <div className="flex w-12 justify-center">{isAccountSelected ? <CircleDot /> : <Circle />}</div>
              <div className="font-roboto flex w-full items-center justify-between gap-2 text-sm">
                <div className="flex flex-col items-start justify-center gap-0.5">
                  <div className="font-semibold">{addressInfo.type.toUpperCase()} </div>
                  {!isAddressTypeSupported && (
                    <span className="text-[0.6rem] leading-none text-red-500">Address type not supported</span>
                  )}
                </div>
                <div className={cn('text-sm font-semibold text-inherit')}>
                  {truncateMidAddressInputValue(account, 16)}
                </div>
              </div>
            </div>
          </Button>
        );
      })}

      {shouldSwitchTo && (
        <Button variant="danger" className="text-sm" onClick={switchNetworkRequest}>
          <div className="flex items-center gap-2">
            Switch to {shouldSwitchTo.text}
            <ArrowRightLeft />
          </div>
        </Button>
      )}
    </>
  );
};

export default HeaderWalletDrawerBtcWalletDetailsContent;
